import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import SecurityIcon from "@mui/icons-material/Security";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ Cloud Computing Content Data
const cloudContent = [
  {
    section: "Cloud Service Models",
    icon: <CloudIcon />,
    items: [
      {
        name: "IaaS",
        description: "Infrastructure as a Service – virtualized computing resources.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Provides virtual machines, storage, and networks",
              "Scalable and on-demand infrastructure",
              "Examples: AWS EC2, Google Compute Engine",
            ],
          },
          {
            heading: "Example Code (Python - boto3 for EC2)",
            code: `import boto3

ec2 = boto3.client("ec2")
response = ec2.describe_instances()
for reservation in response["Reservations"]:
    for instance in reservation["Instances"]:
        print("EC2 Instance ID:", instance["InstanceId"])`,
            language: "python",
          },
          {
            heading: "Example Code (Node.js - AWS SDK for EC2)",
            code: `import AWS from "aws-sdk";

const ec2 = new AWS.EC2();
ec2.describeInstances({}, (err, data) => {
  if (err) console.error(err);
  else {
    data.Reservations.forEach(r =>
      r.Instances.forEach(i => console.log("EC2 Instance:", i.InstanceId))
    );
  }
});`,
            language: "javascript",
          },
        ],
      },
      {
        name: "PaaS",
        description: "Platform as a Service – application development platform.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Includes OS, middleware, and runtime environments",
              "Abstracts infrastructure complexity, lets developers focus on code",
              "Examples: AWS Elastic Beanstalk, Heroku, Google App Engine",
            ],
          },
          {
            heading: "Example Code (Python - Deploy Flask to Heroku)",
            code: `from flask import Flask
app = Flask(__name__)

@app.route("/")
def home():
    return "Hello PaaS!"

if __name__ == "__main__":
    app.run()`,
            language: "python",
          },
          {
            heading: "Example Code (Node.js - Google App Engine)",
            code: `import express from "express";
const app = express();

app.get("/", (req, res) => res.send("Hello from PaaS!"));

app.listen(8080, () => console.log("App running on port 8080"));`,
            language: "javascript",
          },
        ],
      },
      {
        name: "CaaS",
        description: "Container as a Service – container management & orchestration.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Provides container orchestration and scaling",
              "Supports Docker, Kubernetes, and serverless containers",
              "Examples: AWS ECS, Google Kubernetes Engine (GKE), Azure AKS",
            ],
          },
          {
            heading: "Example Code (Python - Deploy Docker Container with ECS)",
            code: `import boto3

ecs = boto3.client("ecs")
response = ecs.list_clusters()
print("ECS Clusters:", response["clusterArns"])`,
            language: "python",
          },
          {
            heading: "Example Code (Node.js - Deploy to Kubernetes with kubernetes-client)",
            code: `import k8s from "@kubernetes/client-node";

const kc = new k8s.KubeConfig();
kc.loadFromDefault();

const k8sApi = kc.makeApiClient(k8s.CoreV1Api);
k8sApi.listPodForAllNamespaces().then(res => {
  res.body.items.forEach(pod =>
    console.log("Pod:", pod.metadata.name)
  );
});`,
            language: "javascript",
          },
        ],
      },
      {
        name: "SaaS",
        description: "Software as a Service – cloud-hosted applications.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Access applications via browser",
              "No local installation required",
              "Examples: Google Workspace, Salesforce",
            ],
          },
        ],
      },
    ],
  },
  {
    section: "Cloud Storage & Databases",
    icon: <StorageIcon />,
    items: [
      {
        name: "AWS S3",
        description: "Scalable object storage service.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Stores any type of data securely",
              "Highly available and durable",
              "Integrates with other AWS services",
            ],
          },
          {
            heading: "Example Code (Python - boto3)",
            code: `import boto3

s3 = boto3.client("s3")
# Upload file
s3.upload_file("local_file.txt", "my-bucket", "remote_file.txt")
# List buckets
for bucket in s3.list_buckets()["Buckets"]:
    print(bucket["Name"])`,
            language: "python",
          },
          {
            heading: "Example Code (Node.js - AWS SDK)",
            code: `import AWS from "aws-sdk";

const s3 = new AWS.S3();
// Upload a file
s3.upload({
  Bucket: "my-bucket",
  Key: "remote_file.txt",
  Body: "Hello Cloud!"
}, (err, data) => {
  if (err) console.error(err);
  else console.log("File uploaded:", data.Location);
});`,
            language: "javascript",
          },
        ],
      },
      {
        name: "Google Cloud Storage",
        description: "Unified object storage for cloud apps.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Supports multiple storage classes",
              "Automatic redundancy and backups",
              "Easy integration with GCP services",
            ],
          },
          {
            heading: "Example Code (Python - google-cloud-storage)",
            code: `from google.cloud import storage

client = storage.Client()
bucket = client.bucket("my-bucket")
blob = bucket.blob("test.txt")
blob.upload_from_filename("local_test.txt")

print("File uploaded to GCP!")`,
            language: "python",
          },
          {
            heading: "Example Code (Node.js - @google-cloud/storage)",
            code: `import { Storage } from "@google-cloud/storage";

const storage = new Storage();
const bucket = storage.bucket("my-bucket");
await bucket.upload("local_test.txt", { destination: "test.txt" });
console.log("File uploaded to GCP!");`,
            language: "javascript",
          },
        ],
      },
      {
        name: "Cloud Databases",
        description: "Managed SQL & NoSQL databases.",
        fullContent: [
          {
            heading: "Examples",
            points: [
              "Amazon RDS (SQL) & DynamoDB (NoSQL)",
              "Google Cloud SQL & Firestore",
              "Azure SQL Database & Cosmos DB",
            ],
          },
        ],
      },
    ],
  },
  {
    section: "Cloud Security",
    icon: <SecurityIcon />,
    items: [
      {
        name: "Identity & Access Management",
        description: "Secure access and user permissions.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Manage users and roles",
              "Control access to cloud resources",
              "Examples: AWS IAM, Azure AD",
            ],
          },
          {
            heading: "Example Code (Python - boto3 IAM)",
            code: `import boto3

iam = boto3.client("iam")
users = iam.list_users()
for user in users["Users"]:
    print("IAM User:", user["UserName"])`,
            language: "python",
          },
        ],
      },
      {
        name: "Encryption",
        description: "Data protection at rest and in transit.",
        fullContent: [
          {
            heading: "Overview",
            points: [
              "Encrypt sensitive data",
              "SSL/TLS for secure transmission",
              "Integrated key management services",
            ],
          },
          {
            heading: "Example Code (Python - boto3 KMS)",
            code: `import boto3

kms = boto3.client("kms")
response = kms.create_key(Description="My KMS Key")
print("Created KMS Key:", response["KeyMetadata"]["KeyId"])`,
            language: "python",
          },
        ],
      },
    ],
  },
];

function CloudComputing() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Cloud Computing
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {cloudContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {cloudContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {cloudContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {cloudContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default CloudComputing;
